import PropTypes from 'prop-types';
import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {GRID_SCHEMA_CLASSES, GridSchemaClass} from 'constants/SampleConstants';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import {
  GRID_SCHEMA_SHAPE,
  GridSchema,
  PLATE_SCHEMA_TYPE_TO_TEXT,
} from 'samples/storables/StorableSchemaConstants';
import SchemasListing from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';

const GRID_SCHEMA_CLASS_TO_SCHEMA_TYPE = {
  [GridSchemaClass.BOX_SCHEMA]: SCHEMA_TYPES.BOX,
  [GridSchemaClass.PLATE_SCHEMA]: SCHEMA_TYPES.PLATE,
};

interface Props {
  gridSchemaClass: GridSchemaClass;
  gridSchemas: Array<GridSchema> | null;
  isWritable: boolean;
  ownerUrlId: string;
  registryId: string;
}

class GridSchemasListing extends React.Component<Props> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    gridSchemaClass: PropTypes.oneOf(GRID_SCHEMA_CLASSES).isRequired,
    gridSchemas: PropTypes.arrayOf(GRID_SCHEMA_SHAPE),
    isWritable: PropTypes.bool.isRequired,
    ownerUrlId: PropTypes.string.isRequired,
    registryId: PropTypes.string.isRequired,
  };

  private handleCreate = (): void => {
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getCreateGridSchemaUrl(
        this.props.ownerUrlId,
        this.props.registryId,
        this.props.gridSchemaClass
      )
    );
  };

  private urlForSchema = (schemaId: string): string =>
    RegistryUtil.getGridSchemaSettingsUrl(
      this.props.ownerUrlId,
      this.props.registryId,
      schemaId,
      this.props.gridSchemaClass
    );

  private handleDelete = (schemaId: string): void => {
    SampleSchemaActions.deleteGridSchema(schemaId, this.props.gridSchemaClass);
  };

  override render(): JSX.Element {
    const isPlateSchema =
      (this.props.gridSchemaClass as string) === (ModelTypeConstants.PLATE_SCHEMA as string);
    const columnHeaders = [
      {name: 'Schemas', width: 3},
      {name: 'Size', width: 2},
      ...(isPlateSchema ? [{name: 'Type', width: 3}] : []),
      {name: 'Fields', width: 4},
    ];
    return (
      <SchemasListing<GridSchema>
        columnHeaders={columnHeaders}
        schemas={this.props.gridSchemas}
        schemaType={GRID_SCHEMA_CLASS_TO_SCHEMA_TYPE[this.props.gridSchemaClass]}
        navigateToCreate={this.handleCreate}
        urlForSchema={this.urlForSchema}
        archiveSchemas={ArchiveActions.bulkArchiveStorableSchemas}
        unarchiveSchemas={ArchiveActions.bulkUnarchiveStorableSchemas}
        deleteSchema={this.handleDelete}
        isWritable={this.props.isWritable}
        mapSchemaToRowValue={(schema) => [
          <span className="u-small-spacing-left">
            {schema.name}
            <ArchiveRecordLabel item={schema} />
          </span>,
          (schema.width * schema.height).toString(),
          ...(isPlateSchema ? [PLATE_SCHEMA_TYPE_TO_TEXT[schema.plateSchemaType!]] : []),
          schema.fields.map(({displayName}) => displayName).join(', '),
        ]}
      />
    );
  }
}

export default GridSchemasListing;
